import {Component, DestroyRef, ElementRef, inject, OnInit, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {UserStatisticsService} from '../../../service/User-statistics-service';
import {AuthService} from '../../../service/auth.service';
import {AllUsersStatisticsModel} from '../../../models/AllUsersStatisticsModel';
import {formatStatisticsDate, statisticsRange, StatisticsRange} from '../../../models/UserStatistics-model';

@Component({
    selector: 'app-all-users-statistics',
    templateUrl: './all-users-statistics.component.html',
    styleUrls: ['./all-users-statistics.component.css']
})
export class AllUsersStatisticsComponent implements OnInit {
    private readonly service = inject(UserStatisticsService);
    private readonly auth = inject(AuthService);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly destroyRef = inject(DestroyRef);
    private request?: Subscription;
    @ViewChild('rangeDialog') private rangeDialog?: ElementRef<HTMLDialogElement>;
    @ViewChild('rangeButton') private rangeButton?: ElementRef<HTMLButtonElement>;
    users: AllUsersStatisticsModel[] = [];
    range: StatisticsRange = statisticsRange();
    loading = false;
    error = '';
    rangeError = '';
    draftRangeError = '';
    editingRange = false;
    draftStart = '';
    draftEnd = '';
    readonly formatDate = formatStatisticsDate;

    isSuperAdmin(): boolean { return this.auth.isSuperAdmin(); }

    ngOnInit(): void {
        if (!this.isSuperAdmin()) return;
        this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
            try {
                this.range = statisticsRange(params.get('startDate'), params.get('endDate'));
                this.rangeError = '';
            } catch (error) {
                this.request?.unsubscribe();
                this.loading = false;
                this.rangeError = (error as Error).message;
                this.users = [];
                return;
            }
            if (!params.get('startDate') || !params.get('endDate')) {
                this.router.navigate([], {relativeTo: this.route, queryParams: this.range, replaceUrl: true});
                return;
            }
            this.load();
        });
    }

    load(): void {
        if (this.rangeError || !this.isSuperAdmin()) return;
        this.request?.unsubscribe();
        this.loading = true;
        this.error = '';
        this.users = [];
        this.request = this.service.getUsers(this.range).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: users => { this.users = users; this.loading = false; },
            error: error => { this.error = this.service.errorMessage(error); this.loading = false; }
        });
    }

    editRange(): void {
        this.draftStart = this.range.startDate.slice(0, 16);
        this.draftEnd = this.range.endDate.slice(0, 16);
        this.draftRangeError = '';
        const dialog = this.rangeDialog?.nativeElement;
        if (!dialog || dialog.open) return;
        const button = this.rangeButton?.nativeElement.getBoundingClientRect();
        dialog.style.top = Math.max(16, Math.min((button?.bottom || 100) + 10, window.innerHeight - 360)) + 'px';
        dialog.showModal();
        this.editingRange = true;
    }

    closeRange(): void {
        this.rangeDialog?.nativeElement.close();
        this.onRangeClosed();
    }

    onRangeClosed(): void {
        this.editingRange = false;
        this.draftRangeError = '';
    }

    onRangeBackdropClick(event: MouseEvent): void {
        const dialog = this.rangeDialog?.nativeElement;
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right ||
            event.clientY < rect.top || event.clientY > rect.bottom) this.closeRange();
    }

    applyRange(): void {
        if (!this.draftStart || !this.draftEnd) {
            this.draftRangeError = 'Choose both a start and end date.';
            return;
        }
        try {
            const range = statisticsRange(this.draftStart, this.draftEnd);
            this.closeRange();
            this.router.navigate([], {relativeTo: this.route, queryParams: range});
        } catch (error) {
            this.draftRangeError = (error as Error).message;
        }
    }

    today(): void {
        this.closeRange();
        this.router.navigate([], {relativeTo: this.route, queryParams: statisticsRange()});
    }

    openFiles(user: AllUsersStatisticsModel, kind: 'pure-original-stats' | 'master-stats'): void {
        const count = kind === 'master-stats' ? user.masterCount : user.pureOriginalCount;
        if (!count || !user.userId || this.loading) return;
        this.router.navigate(['/app/user-statistics', user.userId, kind], {
            queryParams: {...this.range, userName: [user.firstName, user.lastName].filter(Boolean).join(' ')}
        });
    }
}
